import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { TDocument, LockedDialog, TLockedState, isTDocument } from '@modules/documents';
import { SignInContext } from '@modules/users';
import { TFile } from '@modules/files';

import { ToastContext } from '@shared/contexts';

import CommentCard from './CommentCard';
import CommentForm, { schemaComment, TFormDataComment } from './form/CommentForm';
import useGetSelectionInfo from '../hooks/useGetSelectionInfo.hook';
import useUpdateSelection from '../hooks/useUpdateSelection.hook';
import useDeleteSelection from '../hooks/useDeleteSelection.hook';
import { useCreateComment } from '../api';
import useCommentStore from '../stores/useCommentStore';
import { TInlineBlock } from '@modules/inlineBlocks';

type TProps = {
  item: TDocument | TFile | TInlineBlock;
  versionId: string;
  setIsNewCommentOpen: (isNewCommentOpen: boolean) => void;
};

const DEFAULT_VALUES = { text: '', parseDraft: true };

const NewComment = ({ item, versionId, setIsNewCommentOpen }: TProps) => {
  const { user } = useContext(SignInContext);
  const { show } = useContext(ToastContext);

  const getSelectionInfo = useGetSelectionInfo();
  const updateSelection = useUpdateSelection();
  const deleteSelection = useDeleteSelection();
  const createComment = useCreateComment();
  const [lockedState, setLockedState] = useState<TLockedState | undefined>();

  const { setSelectedCommentId } = useCommentStore();

  // Creating a new comment
  const form = useForm<TFormDataComment>({
    resolver: yupResolver(schemaComment),
    defaultValues: DEFAULT_VALUES,
  });
  const {
    handleSubmit,
    reset,
    formState: { isValid },
    watch,
  } = form;

  // "Add to draft" checkbox feature
  const isLatestVersion = versionId === item.versions[0]?.id;
  const hasDraft = isTDocument(item) && Boolean(item.draft);
  // If the document is the latest version and the draft is open, we show the "Add to draft" checkbox
  const withParseDraftCheckbox = isLatestVersion && hasDraft;

  const parseDraft = watch('parseDraft');

  // On submit, we store the comment with __userId__ placeholders
  const handleCommentCreation = (formDataComment: TFormDataComment) => {
    const selectionInfo = getSelectionInfo();

    createComment.mutate(
      {
        itemId: item._id,
        itemType: item.type,
        versionId,
        content: formDataComment.text,
        parseDraft: withParseDraftCheckbox && formDataComment.parseDraft,
        ...selectionInfo,
      },
      {
        onSuccess: (data) => {
          data.lockedUserId;
          if (data.comment) {
            if (data.failedToAddSelection) deleteSelection();
            else updateSelection(data.comment._id);
            setIsNewCommentOpen(false);
            reset(DEFAULT_VALUES);
            setSelectedCommentId(data.comment._id);
          } else if (data.lockedUserId) {
            const newLockedState: TLockedState = {
              isOpen: true,
              lockedUserId: data.lockedUserId,
              message: 'A content-selection can only be added if no user is editing the document.',
              primaryAction: {
                label: 'Add comment without selection',
                onClick: () => {
                  setLockedState({ ...newLockedState, isOpen: true });
                  deleteSelection();
                  handleCommentCreation(formDataComment);
                },
              },
            };
            setLockedState(newLockedState);
          } else {
            // cannot happen:
            show('error', 'Failed to add comment.');
          }
        },
      }
    );
  };

  const handleCancelClick = () => {
    setSelectedCommentId(undefined);
    setIsNewCommentOpen(false);
    // reset({ text: "" });
  };

  useEffect(() => {
    return () => {
      // This will be called on-cancel / on-submit / on-sidebar-close
      deleteSelection();
    };
  }, []);

  // Show info text if the version is not the latest one and not the draft
  const showInfoText = hasDraft ? versionId !== 'draft' && (!isLatestVersion || !parseDraft) : !isLatestVersion;

  return (
    <CommentCard userId={user._id}>
      <LockedDialog state={lockedState} setState={setLockedState} />
      <CommentForm
        form={form}
        isValid={isValid}
        onSubmit={handleSubmit(handleCommentCreation)}
        onDismiss={handleCancelClick}
        withParseDraftCheckbox={withParseDraftCheckbox}
        infoText={showInfoText ? `The comment will only be added to the outdated version ${versionId}.` : undefined}
      />
    </CommentCard>
  );
};

export default NewComment;
